import React from 'react';

import { Divider, Skeleton, Stack, Title, createStyles } from '@mantine/core';
import { IconLock } from '@tabler/icons-react';
import { useAuth } from 'context/auth-context';
import useTotalClasses from 'hooks/reports/utilization/class-usage/useTotalClasses';
import usePackageUtilizationAvgRate from 'hooks/reports/utilization/package-utilization/usePackageUtilizationAvgRate';
import { useNavigate } from 'react-router-dom';

import { useDashboardStyles } from '../../styles';

const useStyles = createStyles(theme => ({
  grid: {
    display: 'grid',
    gridTemplateColumns: `repeat(4, 1fr)`,
    gridTemplateRows: 'auto',
    gridTemplateAreas: '"totalClasses packageUtilization none none"',
    gap: theme.spacing.sm,
    padding: theme.spacing.md,
    [theme.fn.smallerThan('lg')]: {
      gridTemplateColumns: `repeat(4, 1fr)`,
      gridTemplateAreas: '"totalClasses packageUtilization none none"',
    },
    [theme.fn.smallerThan('sm')]: {
      gridTemplateColumns: 'repeat(2, 1fr)',
      gridTemplateAreas: '"totalClasses packageUtilization"',
    },
  },
  classUsage: {
    gridArea: 'totalClasses',
  },
  packageUtilization: {
    gridArea: 'packageUtilization',
  },
}));

const UtilizationCard = (): JSX.Element => {
  const { classes: dashboard } = useDashboardStyles();
  const { classes } = useStyles();

  const navigate = useNavigate();

  const { hasPerm } = useAuth();
  const isUtilizationViewable = hasPerm('UtilizationReportsView');

  const {
    data: totalClasses,
    isLoading: totalClassesIsLoading,
    isError: totalClassesIsError,
    isSuccess: totalClassesIsSuccess,
  } = useTotalClasses();

  const {
    data: avgPackageUtilizationRate,
    isLoading: avgPackageUtilizationRateIsLoading,
    isError: avgPackageUtilizationRateIsError,
    isSuccess: avgPackageUtilizationRateIsSuccess,
  } = usePackageUtilizationAvgRate();

  const handleReportClick = (report: string): void => {
    if (isUtilizationViewable) {
      navigate(`/reports/${report}`);
    }
  };

  const getTotalClassesCount = (): JSX.Element => {
    if (!isUtilizationViewable) {
      return <IconLock size='2rem' />;
    }
    if (totalClassesIsLoading) {
      return <Skeleton height='2rem' width='75%' />;
    }
    if (totalClassesIsError) {
      return <div className={dashboard.value}>Error</div>;
    }
    if (totalClassesIsSuccess && totalClasses) {
      return <div className={dashboard.value}>{totalClasses.data.count}</div>;
    }
    return <></>;
  };

  const getAvgPackageUtilizationRate = (): JSX.Element => {
    if (!isUtilizationViewable) {
      return <IconLock size='2rem' />;
    }
    if (avgPackageUtilizationRateIsLoading) {
      return <Skeleton height='2rem' width='75%' />;
    }
    if (avgPackageUtilizationRateIsError) {
      return <div className={dashboard.value}>Error</div>;
    }
    if (avgPackageUtilizationRateIsSuccess && avgPackageUtilizationRate.data.utilizationRate === null) {
      return <div className={dashboard.valueText}>Click to Add Data</div>;
    }
    if (avgPackageUtilizationRateIsSuccess && avgPackageUtilizationRate.data.utilizationRate !== null) {
      const rate = Math.round(avgPackageUtilizationRate.data.utilizationRate * 100).toFixed(1);
      return <div className={dashboard.value}>{rate}%</div>;
    }
    return <></>;
  };

  return (
    <div className={`${dashboard.utilization} ${dashboard.card}`}>
      <div className={dashboard.cardHeader}>
        <Title order={4}>Utilization</Title>
      </div>

      <Divider />

      <div className={classes.grid}>
        <Stack
          spacing={0}
          p='xs'
          className={`${classes.classUsage} ${isUtilizationViewable ? dashboard.clickableCard : undefined}`}
          onClick={() => handleReportClick('class-usage')}
        >
          <div className={dashboard.label}>
            <Stack spacing={0}>
              <div>Total Classes</div>
              <div>Last Week</div>
            </Stack>
          </div>
          {getTotalClassesCount()}
        </Stack>

        <Stack
          spacing={0}
          p='xs'
          className={`${classes.packageUtilization} ${
            isUtilizationViewable ? dashboard.clickableCard : undefined
          }`}
          onClick={() => handleReportClick('package-utilization')}
        >
          <div className={dashboard.label}>
            <Stack spacing={0}>
              <div>Avg. Package</div>
              <div>Utilization Rate</div>
            </Stack>
          </div>
          {getAvgPackageUtilizationRate()}
        </Stack>
      </div>
    </div>
  );
};

export default UtilizationCard;
